import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { Box, IconButton, Input, Separator, Stack, Text } from '@chakra-ui/react';

import { Button } from '@m/components-ui/button';
import { DialogContent, DialogRoot } from '@m/components-ui/dialog';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@m/components-ui/popover';
import {
  Close,
  EmojiEvents,
  Error as ErrorIcon,
  History,
  LocalParking,
  LocationOn,
  NearMe,
  Place,
  Search,
  Stadium,
} from '@m/icons';
import { LocationSearchParams, LocationSuggestionType } from '@m/types/api/LocationSuggestion';
import { eventPrefillReservationWindow, parseDeepLinkDateTime } from '@m/utils/dateTimeUtils';

import { isSyntheticLocationLabel, PARKING_NEAR_ME } from '../utils';

export interface SearchDestination {
  address?: string;
  center: [number, number];
  eventEnd?: string;
  eventStart?: string;
  /**
   * When true, committing this event keeps the cushioned date window editable
   * (set for picks from the venue upcoming-events dialog).
   */
  flexibleEventDates?: boolean;
  id: string;
  /** Mapbox city and state, e.g. "New York, NY". Omitted for non-Mapbox rows. */
  locality?: string;
  /**
   * Badge copy for venue event rows, e.g. "Parking starting at $13".
   * Omitted when the API has not configured a starting rate.
   */
  parkingLabel?: string;
  place_name: string;
  relatedVenueId?: string;
  searchParams?: LocationSearchParams;
  suggestionType?: LocationSuggestionType;
  text: string;
  /** Venue display name for event suggestions (shown in event chrome). */
  venueName?: string;
}

export interface ReservationSearchProps {
  /** When false, hides clear controls on the location field. Defaults to false. */
  allowClearLocation?: boolean;
  /**
   * When true, Clear (X) commits an empty field (no blur-restore of the prior label).
   * Home uses this so Clear fully resets the search form.
   */
  clearCommitsEmpty?: boolean;
  /** Use the 48px desktop navigation field height instead of the default 56px. */
  compact?: boolean;
  destinations: SearchDestination[];
  /**
   * Dropdown only: omit the field chrome (border/background/height) so the control can sit
   * inside a shared nav pill. Keeps the leading search icon, input, and clear button.
   */
  embedded?: boolean;
  /** Inline validation message shown under the field (e.g. empty location on Find parking). */
  errorText?: string;
  /**
   * Field variant only: second line rendered under the location text when
   * `selectedSuggestionType` is 'event' (e.g. "Aug 10, 12:00 PM – 2:00 PM" for
   * the locked reservation window). Omit to keep the single-line field.
   */
  eventTimeLabel?: string;
  /**
   * Render only the full-screen Where to dialog. The update sheet owns the
   * closed field and opens this when that field is tapped.
   */
  hideTrigger?: boolean;
  /** When true, shows the error border (and `errorText` when provided). */
  invalid?: boolean;
  /**
   * When provided, tapping the search bar runs this instead of opening the built-in
   * location dialog — used to open the Find parking form, keeping the entry point
   * consistent across the landing page and the map.
   */
  onActivate?: () => void;
  onClearSearch?: () => void;
  /** Notified when the search surface opens/closes. Pair with `open` for controlled use. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Restores an abandoned draft. Prefer this over `onSearchQueryChange` so the
   * parent can also restore the committed destination pin.
   */
  onRestoreSearchQuery?: (value: string) => void;
  /** Returns `false` when the search could not commit (debounce pending / no results). */
  onSearch: () => boolean | void;
  onSearchQueryChange: (value: string) => void;
  onSelectDestination: (dest: SearchDestination) => void;
  onUseCurrentLocation?: () => void;
  /** Controls the open state of the search surface. Omit to let the component manage it. */
  open?: boolean;
  /**
   * Dropdown only: float `errorText` under the field instead of stacking it in flow, so a
   * bottom-aligned row of fields (landing hero) keeps its baseline when the error appears.
   */
  overlayErrorText?: boolean;
  /** Location field placeholder. Defaults to "Enter an Address, City, Venue or Event". */
  placeholder?: string;
  /** 'dialog' (mobile, full-screen) or 'dropdown' (desktop, anchored popover). */
  presentation?: 'dialog' | 'dropdown';
  /** Previously selected destinations, most-recent first — clicking one selects it immediately. */
  recentSearches?: SearchDestination[];
  searchQuery: string;
  /**
   * When set, replaces the leading search icon on the closed field (trophy for a
   * committed event, stadium for a venue). Cleared when the user edits the query.
   */
  selectedSuggestionType?: LocationSuggestionType;
  /** Shows a non-blocking empty result row after enhanced suggestions settle. */
  showEmptyState?: boolean;
  /**
   * Landing hero (dropdown presentation): render a trailing brand search button and drop the
   * leading search icon. Defaults off so the nav search is unchanged.
   */
  showSearchButton?: boolean;
  /**
   * `hero` (default): prominent bar with a brand search button (landing page).
   * `field`: form-input style with a leading search icon and a clear (X) button.
   */
  triggerVariant?: 'hero' | 'field';
}

const HighlightedText = ({ text, query }: { query: string; text: string }) => {
  const matchIndex = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (matchIndex === -1) {
    return (
      <Text variant="MainBodyRegular" color="text.primary" truncate flex="1" textAlign="left">
        {text}
      </Text>
    );
  }
  return (
    <Text variant="MainBodyRegular" color="text.primary" truncate flex="1" textAlign="left">
      {text.slice(0, matchIndex)}
      {/* Figma emphasises the matched run in Main Body/Medium, not DemiBold. */}
      <Text as="span" variant="MainBodyMedium" color="text.primary">
        {text.slice(matchIndex, matchIndex + query.length)}
      </Text>
      {text.slice(matchIndex + query.length)}
    </Text>
  );
};

/**
 * Figma "Leading icons" slot (node I313:31409;6861:58302): a 24px box around a
 * 16px glyph, so every row's label starts at the same 48px inset whether or not
 * the glyph is square. The box is also what sets the row's height — 24px here
 * plus the row's 12px padding is the node's 48px, taller than the 22px label.
 */
const LeadingIcon = ({ children }: { children: React.ReactNode }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    boxSize="6"
    fontSize="16px"
    lineHeight="1"
    flexShrink={0}
  >
    {children}
  </Box>
);

const ListItem = ({ onClick, children }: { children: React.ReactNode; onClick: () => void }) => (
  <Box
    asChild
    display="flex"
    alignItems="center"
    gap="2"
    px="4"
    py="3"
    w="100%"
    cursor="pointer"
    _hover={{ bg: 'fill.interactive.hover' }}
  >
    {/* preventDefault on mousedown keeps input focus until click commits — avoids
        blur-restore racing the destination selection. */}
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {children}
    </button>
  </Box>
);

/** 4px spacer + 1px rule closing a section (Figma "Location List item" bottom spacer + divider). */
const SectionDivider = () => (
  <Box pt="1">
    <Separator />
  </Box>
);

const ResultsSectionHeader = ({ label }: { label: string }) => (
  <Box px="4" pt="4" pb="2">
    <Text variant="SmallBodyRegular" color="text.secondary">
      {label}
    </Text>
  </Box>
);

type SuggestionBucket = 'events' | 'parkingLots' | 'places' | 'venues';

function bucketForSuggestionType(type: LocationSuggestionType | undefined): SuggestionBucket {
  if (type === 'venue') return 'venues';
  if (type === 'event') return 'events';
  if (type === 'zone') return 'parkingLots';
  return 'places';
}

// Per-bucket display caps — the backend/Mapbox call itself is unbounded (or capped
// unbalanced across types), so the client caps each section to keep the dropdown short.
const MAX_PLACES_SHOWN = 5;
const MAX_PARKING_LOTS_SHOWN = 3;
const MAX_VENUES_SHOWN = 3;
const MAX_EVENTS_SHOWN = 3;

function eventStartMs(dest: SearchDestination): number {
  if (!dest.eventStart) return Infinity;
  const start = parseDeepLinkDateTime(dest.eventStart);
  // Missing/unparseable start sorts last rather than floating to the top.
  return start ? start.getTime() : Infinity;
}

/**
 * Drop events whose Starts/Ends prefill is no longer bookable. Wall-clock+`Z`
 * times must go through `parseDeepLinkDateTime`; missing ends use the same
 * default duration as `eventPrefillReservationWindow` (no parking after-cushion).
 */
function isPastEvent(dest: SearchDestination, now: Date): boolean {
  if (!dest.eventStart) return false;
  const eventStart = parseDeepLinkDateTime(dest.eventStart);
  if (!eventStart) return false;
  const eventEnd = dest.eventEnd ? parseDeepLinkDateTime(dest.eventEnd) : undefined;
  if (dest.eventEnd && !eventEnd) return false;
  return !eventPrefillReservationWindow(eventStart, eventEnd, now);
}

/**
 * Display order for free-text results (Places → Parking lots → Venues → Events),
 * each capped to its own max, with events additionally date-sorted and past events dropped.
 * Keep Enter / Find parking in sync with the first visible suggestion row.
 */
export function orderSearchDestinationsForDisplay(
  destinations: SearchDestination[],
): SearchDestination[] {
  const places = destinations
    .filter((dest) => bucketForSuggestionType(dest.suggestionType) === 'places')
    .slice(0, MAX_PLACES_SHOWN);
  const parkingLots = destinations
    .filter((dest) => bucketForSuggestionType(dest.suggestionType) === 'parkingLots')
    .slice(0, MAX_PARKING_LOTS_SHOWN);
  const venues = destinations
    .filter((dest) => bucketForSuggestionType(dest.suggestionType) === 'venues')
    .slice(0, MAX_VENUES_SHOWN);
  const now = new Date();
  const events = destinations
    .filter((dest) => bucketForSuggestionType(dest.suggestionType) === 'events')
    .filter((dest) => !isPastEvent(dest, now))
    .sort((a, b) => eventStartMs(a) - eventStartMs(b))
    .slice(0, MAX_EVENTS_SHOWN);
  return [...places, ...parkingLots, ...venues, ...events];
}

function renderPlaceIcon(type: LocationSuggestionType | undefined) {
  return (
    <LeadingIcon>
      {type === 'poi' ? (
        <Place color="fill.icon.descriptive" />
      ) : (
        <LocationOn color="fill.icon.descriptive" />
      )}
    </LeadingIcon>
  );
}

/** "10/01" from an ISO event start — mirrors the compact date shown next to the title in Figma. */
function formatEventBadgeDate(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const date = parseDeepLinkDateTime(iso);
  if (!date) return undefined;
  return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit' }).format(date);
}

function PlaceResult({ destination, query }: { destination: SearchDestination; query: string }) {
  return (
    <>
      {renderPlaceIcon(destination.suggestionType)}
      {destination.locality ? (
        <Stack gap="0" minW="0" flex="1">
          <HighlightedText text={destination.text} query={query} />
          <Text variant="SmallBodyRegular" color="text.secondary" truncate textAlign="left">
            {destination.locality}
          </Text>
        </Stack>
      ) : (
        <HighlightedText text={destination.text} query={query} />
      )}
    </>
  );
}

function ParkingLotResult({
  destination,
  query,
}: {
  destination: SearchDestination;
  query: string;
}) {
  return (
    <>
      <LeadingIcon>
        <LocalParking color="fill.icon.descriptive" />
      </LeadingIcon>
      <HighlightedText text={destination.address ?? destination.text} query={query} />
    </>
  );
}

function VenueResult({ destination, query }: { destination: SearchDestination; query: string }) {
  return (
    <>
      <LeadingIcon>
        <Stadium color="fill.icon.descriptive" />
      </LeadingIcon>
      <HighlightedText text={destination.place_name} query={query} />
    </>
  );
}

function EventResult({ destination, query }: { destination: SearchDestination; query: string }) {
  const badgeDate = formatEventBadgeDate(destination.eventStart);
  return (
    <>
      <LeadingIcon>
        <EmojiEvents color="fill.icon.descriptive" />
      </LeadingIcon>
      <Stack gap="0" minW="0" flex="1">
        <Box display="flex" alignItems="baseline" gap="1" minW={0}>
          <HighlightedText text={destination.text} query={query} />
          {badgeDate && (
            <Text variant="SmallBodyMedium" color="brand.400" flexShrink={0}>
              {badgeDate}
            </Text>
          )}
        </Box>
        {destination.address && (
          <Text variant="SmallBodyRegular" color="text.secondary" truncate textAlign="left">
            {destination.address}
          </Text>
        )}
        {destination.parkingLabel && (
          <Text variant="SmallBodyRegular" color="text.secondary" truncate textAlign="left">
            {destination.parkingLabel}
          </Text>
        )}
      </Stack>
    </>
  );
}

/** Leading icon for a committed search: event trophy, venue stadium, else search. */
function suggestionTypeLeadingIcon(type?: LocationSuggestionType) {
  if (type === 'event') {
    return <EmojiEvents color="fill.icon.descriptive" aria-hidden />;
  }
  if (type === 'venue') {
    return <Stadium color="fill.icon.descriptive" aria-hidden />;
  }
  return <Search color="fill.icon.descriptive" aria-hidden />;
}

export const ReservationSearch = ({
  searchQuery,
  onActivate,
  onSearchQueryChange,
  onRestoreSearchQuery,
  allowClearLocation = false,
  clearCommitsEmpty = false,
  onClearSearch,
  onOpenChange,
  onSearch,
  open,
  destinations,
  onSelectDestination,
  onUseCurrentLocation,
  hideTrigger = false,
  presentation = 'dialog',
  placeholder = 'Enter an Address, City, Venue or Event',
  recentSearches = [],
  triggerVariant = 'hero',
  selectedSuggestionType,
  showSearchButton = false,
  embedded = false,
  compact = false,
  invalid = false,
  errorText,
  eventTimeLabel,
  overlayErrorText = false,
  showEmptyState = false,
}: ReservationSearchProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  // Controlled when `open` is provided; otherwise the component owns the state.
  const isOpen = open ?? uncontrolledOpen;

  // Draft field: snapshot on edit start. Dismiss without selecting a destination
  // (or Search / near-me) restores the snapshot — including after Clear (X).
  // Only an explicit commit permanently replaces the committed location text.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  /** Full-screen Where-to shell (portaled) — exclude from "outside" dismiss. */
  const dialogContentRef = useRef<HTMLDivElement | null>(null);
  const searchQueryRef = useRef(searchQuery);
  const draftSnapshotRef = useRef(searchQuery);
  const didCommitRef = useRef(false);
  /** True while the user is editing a draft (focus → commit/dismiss). */
  const editSessionActiveRef = useRef(false);
  /**
   * After a destination commit the full-screen dialog unmounts under the same
   * tap; without this window that tap can hit the trigger and reopen "Where to?".
   */
  const suppressActivateUntilRef = useRef(0);
  /** Consume the next dismiss-restore so Clear (X) is not immediately undone by blur. */
  const skipDismissRestoreRef = useRef(false);
  /**
   * True once the user has typed without committing. The parent holds
   * `selectedSuggestionType` until a new pick lands, so the leading icon follows
   * this instead — see `leadingSuggestionType`.
   */
  const [draftEdited, setDraftEdited] = useState(false);
  const errorTextId = useId();

  const markCommittedAndClose = useCallback(() => {
    didCommitRef.current = true;
    editSessionActiveRef.current = false;
    setDraftEdited(false);
    suppressActivateUntilRef.current = Date.now() + 500;
    setUncontrolledOpen(false);
    onOpenChange?.(false);
    // List items use mousedown preventDefault so the input stays focused after a
    // pick. Blur so the next tap re-fires onFocus and can reopen suggestions.
    inputRef.current?.blur();
  }, [onOpenChange]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
    // External commits (near-me / geo label) update the restore snapshot when
    // the user is not mid-edit, so dismiss does not revive a stale label.
    if (!editSessionActiveRef.current) {
      // Park Now: Clear can empty `searchQuery` before focus marks the draft
      // session active — never clobber a non-empty restore snapshot with "".
      // Home (`clearCommitsEmpty`): empty is the committed reset target.
      if (searchQuery === '' && draftSnapshotRef.current !== '' && !clearCommitsEmpty) {
        return;
      }
      draftSnapshotRef.current = searchQuery;
    }
  }, [clearCommitsEmpty, searchQuery]);

  // Mobile Where-to dialog: ensure the editable input receives focus when opened
  // (autoFocus alone is flaky with the dialog portal on iOS).
  useEffect(() => {
    if (!isOpen || presentation === 'dropdown') return undefined;
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, presentation]);

  const beginDraftEdit = useCallback(() => {
    // After Clear, we refocus the empty field — don't overwrite the restore
    // snapshot with "" (that would make click-outside keep the field empty).
    if (!didCommitRef.current && searchQueryRef.current !== draftSnapshotRef.current) {
      editSessionActiveRef.current = true;
      return;
    }
    draftSnapshotRef.current = searchQueryRef.current;
    didCommitRef.current = false;
    editSessionActiveRef.current = true;
  }, []);

  const restoreDraftIfNeeded = useCallback(
    (options?: { fromBlur?: boolean }) => {
      if (didCommitRef.current) return;
      // Clear (X) blurs the input on iOS even with mousedown preventDefault.
      // Ignore that one restore so the field stays empty for typing.
      if (skipDismissRestoreRef.current) {
        skipDismissRestoreRef.current = false;
        // Desktop Clear uses mousedown preventDefault, so blur never runs and
        // this one-shot can still be armed. Only ignore restore on the spurious
        // post-Clear blur when the field is still empty; outside dismiss must
        // still restore the pre-Clear snapshot.
        if (options?.fromBlur && searchQueryRef.current === '') return;
      }
      const snapshot = draftSnapshotRef.current;
      // The field is back on the committed label, so the committed icon applies again.
      setDraftEdited(false);
      if (searchQueryRef.current !== snapshot) {
        (onRestoreSearchQuery ?? onSearchQueryChange)(snapshot);
      }
    },
    [onRestoreSearchQuery, onSearchQueryChange],
  );

  const dismissDraft = useCallback(
    (options?: { fromBlur?: boolean }) => {
      if (didCommitRef.current) return;
      restoreDraftIfNeeded(options);
      editSessionActiveRef.current = false;
      // Dropdown: dismiss closes the popover. Full-screen dialog must stay open
      // (Clear / in-dialog blur used to call this and slam Where to? shut).
      if (presentation !== 'dropdown') return;
      suppressActivateUntilRef.current = Date.now() + 500;
      setUncontrolledOpen(false);
      onOpenChange?.(false);
    },
    [onOpenChange, presentation, restoreDraftIfNeeded],
  );

  const setIsOpen = useCallback(
    (next: boolean) => {
      if (!next) {
        // Close (X) / dialog dismiss: suppress so the tap does not reopen via the trigger.
        suppressActivateUntilRef.current = Date.now() + 500;
        restoreDraftIfNeeded();
        editSessionActiveRef.current = false;
      }
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange, restoreDraftIfNeeded],
  );

  // Tapping the bar: open Find parking form when `onActivate` is set (park-now),
  // otherwise open the built-in Where-to dialog so the user can type.
  const activate = useCallback(() => {
    if (Date.now() < suppressActivateUntilRef.current) return;
    beginDraftEdit();
    if (onActivate) {
      onActivate();
      return;
    }
    setIsOpen(true);
  }, [onActivate, setIsOpen, beginDraftEdit]);

  const handleSelectDestination = useCallback(
    (dest: SearchDestination) => {
      // Navigate / commit before closing — unmounting the dialog first can drop
      // the rest of the click handler on some mobile WebViews.
      onSelectDestination(dest);
      markCommittedAndClose();
    },
    [markCommittedAndClose, onSelectDestination],
  );

  const handleSearch = useCallback(() => {
    const committed = onSearch();
    if (committed === false) {
      // No list hit — drop invalid draft text and keep the last committed label.
      restoreDraftIfNeeded();
      return;
    }
    markCommittedAndClose();
  }, [markCommittedAndClose, onSearch, restoreDraftIfNeeded]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!onUseCurrentLocation) return;
    // Lock commit + snapshot BEFORE parent state updates / popover close, so a
    // blur or dismiss-restore cannot revive the empty pre-click draft and wipe
    // "Parking near me" after Use my current location.
    didCommitRef.current = true;
    draftSnapshotRef.current = PARKING_NEAR_ME;
    searchQueryRef.current = PARKING_NEAR_ME;
    editSessionActiveRef.current = false;
    setDraftEdited(false);
    onUseCurrentLocation();
    // Close without restoreDraftIfNeeded (setIsOpen(false) would restore).
    suppressActivateUntilRef.current = Date.now() + 500;
    setUncontrolledOpen(false);
    onOpenChange?.(false);
    inputRef.current?.blur();
  }, [onOpenChange, onUseCurrentLocation]);

  const showClearButton = allowClearLocation && !!searchQuery && !!onClearSearch;

  const handleClearSearch = useCallback(
    (e?: { stopPropagation: () => void }) => {
      e?.stopPropagation();
      // The click that hits X often blurs the input first (iOS). Skip that restore.
      skipDismissRestoreRef.current = true;
      // Home: Clear resets to empty and starts a fresh draft session. Snapshot stays
      // "" so click-outside after typing again does not revive the prior label.
      // Keep didCommit=false so dismiss still runs (and restores to empty).
      if (clearCommitsEmpty) {
        draftSnapshotRef.current = '';
        didCommitRef.current = false;
        editSessionActiveRef.current = true;
        onClearSearch?.();
        // Clear often runs while the input is still focused (closed after a pick),
        // so onFocus will not re-fire — open the suggestions surface explicitly.
        setUncontrolledOpen(true);
        onOpenChange?.(true);
        requestAnimationFrame(() => {
          inputRef.current?.focus({ preventScroll: true });
        });
        return;
      }
      // X uses mousedown preventDefault, so the input may never focus and
      // didCommit can still be true from the last select — snapshot first.
      const current = searchQueryRef.current;
      const alreadyDirtyDraft = !didCommitRef.current && current !== draftSnapshotRef.current;
      if (current && !alreadyDirtyDraft) {
        draftSnapshotRef.current = current;
      }
      didCommitRef.current = false;
      // Mark the draft session before clearing parent state so the searchQuery
      // sync effect cannot replace the restore snapshot with "".
      editSessionActiveRef.current = true;
      // Clear only empties the draft text. The committed search (location, dates,
      // URL params, results) stays until a new destination is picked, so clicking
      // outside restores the label and nothing else has been lost.
      onSearchQueryChange('');
      setUncontrolledOpen(true);
      onOpenChange?.(true);
      // Keep focus so click-on-map / outside blurs and restores the snapshot.
      requestAnimationFrame(() => {
        inputRef.current?.focus({ preventScroll: true });
      });
    },
    [clearCommitsEmpty, onClearSearch, onOpenChange, onSearchQueryChange],
  );

  // Keep "Parking near me" / map-area labels in the field; user can edit in place.
  const handleSearchFocus = useCallback(() => {
    if (Date.now() < suppressActivateUntilRef.current) return;
    beginDraftEdit();
    setIsOpen(true);
    // Select the synthetic label so the next keystroke replaces it instead of
    // appending (desktop + mobile Where-to).
    if (isSyntheticLocationLabel(searchQueryRef.current)) {
      requestAnimationFrame(() => {
        inputRef.current?.select();
      });
    }
    // Dropdown: scroll the field toward the viewport center so the results
    // popover below it isn't clipped by the bottom of the window (e.g. the
    // marketing hero's search field sits near the fold).
    if (presentation === 'dropdown') {
      requestAnimationFrame(() => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [setIsOpen, beginDraftEdit, presentation]);

  // Desktop: popover may not be mounted when there are no suggestions, so blur
  // still needs to dismiss + restore an uncommitted draft.
  // Mobile dialog: ignore blur while focus stays inside the dialog (Clear, Close, list).
  const handleSearchBlur = useCallback(() => {
    window.setTimeout(() => {
      // Focus moved into the clear button / suggestions — stay in draft mode.
      if (inputRef.current && document.activeElement === inputRef.current) return;
      const active = document.activeElement;
      if (presentation !== 'dropdown' && active && dialogContentRef.current?.contains(active)) {
        return;
      }
      dismissDraft({ fromBlur: true });
    }, 0);
  }, [dismissDraft, presentation]);

  // Mapbox canvas clicks often don't blur the input. While the field differs from
  // the draft snapshot, restore on outside pointerdown (ignore portaled suggestions
  // and the full-screen Where-to dialog content).
  useEffect(() => {
    if (didCommitRef.current) return undefined;
    if (searchQuery === draftSnapshotRef.current) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (didCommitRef.current) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (popoverContentRef.current?.contains(target)) return;
      if (dialogContentRef.current?.contains(target)) return;
      dismissDraft();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [searchQuery, dismissDraft]);

  // Backspace edits `searchQuery` alone, so the trophy/stadium would otherwise
  // outlive the text it described. Any uncommitted keystroke — or an emptied
  // field — falls back to the search icon, the same as Clear. Tracking the edit
  // rather than diffing against a snapshot of the committed label matters: the
  // snapshot can only be taken after the parent has already rendered the new
  // label, which shows the fallback icon for a frame on every fresh commit.
  const leadingSuggestionType =
    draftEdited || !searchQuery.trim() ? undefined : selectedSuggestionType;

  // Only show when a handler exists — Home wires one with geo or region fallback.
  const showNearMeRow = !!onUseCurrentLocation;

  // Section headers only appear once venue/event suggestions are in the mix — the
  // plain Mapbox-only list (free-text search gate off) keeps its original flat look.
  const places = destinations.filter(
    (dest) => bucketForSuggestionType(dest.suggestionType) === 'places',
  );
  const venues = destinations.filter(
    (dest) => bucketForSuggestionType(dest.suggestionType) === 'venues',
  );
  const events = destinations.filter(
    (dest) => bucketForSuggestionType(dest.suggestionType) === 'events',
  );
  const parkingLots = destinations.filter(
    (dest) => bucketForSuggestionType(dest.suggestionType) === 'parkingLots',
  );
  const hasGroupedResults = venues.length > 0 || events.length > 0 || parkingLots.length > 0;

  // Recents are for the empty field only — once the user types, live suggestions take over.
  const visibleRecentSearches = searchQuery.trim() ? [] : recentSearches;

  // Shared between the mobile dialog and desktop dropdown — same items, just a different shell.
  const sections: React.ReactNode[] = [];
  if (visibleRecentSearches.length > 0) {
    sections.push(
      <Fragment key="recent">
        <ResultsSectionHeader label="Recent searches" />
        {visibleRecentSearches.map((recent) => (
          <ListItem key={recent.id} onClick={() => handleSelectDestination(recent)}>
            <LeadingIcon>
              <History color="fill.icon.descriptive" />
            </LeadingIcon>
            <HighlightedText text={recent.text} query={searchQuery} />
          </ListItem>
        ))}
      </Fragment>,
    );
  }
  if (places.length > 0) {
    sections.push(
      <Fragment key="places">
        {hasGroupedResults && <ResultsSectionHeader label="Places" />}
        {places.map((dest) => (
          <ListItem key={dest.id} onClick={() => handleSelectDestination(dest)}>
            <PlaceResult destination={dest} query={searchQuery} />
          </ListItem>
        ))}
      </Fragment>,
    );
  }
  if (parkingLots.length > 0) {
    sections.push(
      <Fragment key="parkingLots">
        <ResultsSectionHeader label="Parking lots" />
        {parkingLots.map((dest) => (
          <ListItem key={dest.id} onClick={() => handleSelectDestination(dest)}>
            <ParkingLotResult destination={dest} query={searchQuery} />
          </ListItem>
        ))}
      </Fragment>,
    );
  }
  if (venues.length > 0) {
    sections.push(
      <Fragment key="venues">
        <ResultsSectionHeader label="Venues" />
        {venues.map((dest) => (
          <ListItem key={dest.id} onClick={() => handleSelectDestination(dest)}>
            <VenueResult destination={dest} query={searchQuery} />
          </ListItem>
        ))}
      </Fragment>,
    );
  }
  if (events.length > 0) {
    sections.push(
      <Fragment key="events">
        <ResultsSectionHeader label="Events" />
        {events.map((dest) => (
          <ListItem key={dest.id} onClick={() => handleSelectDestination(dest)}>
            <EventResult destination={dest} query={searchQuery} />
          </ListItem>
        ))}
      </Fragment>,
    );
  }

  const resultsList = (
    <>
      {showNearMeRow && (
        <>
          <Box pt="1">
            <ListItem onClick={handleUseCurrentLocation}>
              <LeadingIcon>
                <NearMe color="brand.400" />
              </LeadingIcon>
              <Text variant="MainBodyRegular" color="brand.400">
                Use my current location
              </Text>
            </ListItem>
          </Box>
          {sections.length > 0 && <SectionDivider />}
        </>
      )}

      {sections.map((section, index) => (
        <Fragment key={index}>
          {index > 0 && <SectionDivider />}
          {section}
        </Fragment>
      ))}

      {showEmptyState && (
        <Box px="4" py="6">
          <Text variant="MainBodyRegular" color="text.secondary" textAlign="center">
            No locations found
          </Text>
        </Box>
      )}
    </>
  );

  if (presentation === 'dropdown') {
    // Home: open on empty field even before near-me/results are ready.
    const hasResults =
      showNearMeRow ||
      clearCommitsEmpty ||
      destinations.length > 0 ||
      showEmptyState ||
      visibleRecentSearches.length > 0;

    let dropdownBorderColor: string | undefined;
    if (!embedded) {
      if (invalid) dropdownBorderColor = 'stroke.border.error';
      else dropdownBorderColor = isOpen ? 'stroke.border.focused' : 'stroke.border.input';
    }

    // Out of flow, keeping the `gap="1"` spacing it had in flow: the hero row is
    // bottom-aligned, so any height added under the field lifts the field off the
    // row's baseline.
    const errorOverlayProps = overlayErrorText
      ? ({ position: 'absolute', top: '100%', mt: '1' } as const)
      : undefined;

    return (
      <Stack
        ref={rootRef}
        gap="1"
        w="full"
        minW={0}
        position={overlayErrorText ? 'relative' : undefined}
      >
        <PopoverRoot
          open={isOpen && hasResults}
          onOpenChange={({ open: nextOpen }) => {
            if (nextOpen) {
              setIsOpen(true);
              return;
            }
            // After a list/near-me commit, only close — do not run dismiss-restore
            // (that was wiping "Parking near me" on Home).
            if (didCommitRef.current) {
              suppressActivateUntilRef.current = Date.now() + 500;
              setUncontrolledOpen(false);
              return;
            }
            // When the field is focused but there is nothing to list yet (empty
            // query, geo still resolving), `open={isOpen && hasResults}` is false.
            // Ignore that library sync close — otherwise it clears isOpen and sets
            // suppressActivate, so the suggestions never appear once results arrive.
            if (!hasResults && document.activeElement === inputRef.current) {
              return;
            }
            setIsOpen(false);
          }}
          autoFocus={false}
          positioning={{ placement: 'bottom-start', sameWidth: true, gutter: 4 }}
        >
          <PopoverTrigger asChild>
            <Box
              display="flex"
              alignItems="center"
              gap="2"
              bg={embedded ? 'transparent' : 'fill.input.filled'}
              // Keep 2px always when chrome is shown — swapping 1px↔2px shifts the
              // field and can dismiss the suggestions popover mid-click.
              borderWidth={embedded ? '0' : '2px'}
              borderColor={dropdownBorderColor}
              borderRadius={embedded ? undefined : '6px'}
              pl="4"
              pr={showSearchButton ? '2.5' : '4'}
              h={compact || embedded ? '48px' : '56px'}
              w="full"
              minW={0}
              cursor="text"
            >
              {!showSearchButton && suggestionTypeLeadingIcon(leadingSuggestionType)}
              <Input
                ref={inputRef}
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => {
                  setDraftEdited(true);
                  onSearchQueryChange(e.target.value);
                  // After a list pick the field can stay focused with the popover
                  // closed; typing must reopen suggestions without requiring blur.
                  if (!isOpen) {
                    beginDraftEdit();
                    setIsOpen(true);
                  }
                }}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                // The input lives inside the PopoverTrigger, which toggles on click.
                // Stop that toggle, but if the field is already focused and closed
                // (post-commit), reopen — onFocus will not fire again.
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isOpen && Date.now() >= suppressActivateUntilRef.current) {
                    beginDraftEdit();
                    setIsOpen(true);
                  }
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                variant="flushed"
                border="none"
                bg="transparent"
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                flex="1"
                minW={0}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid && errorText ? errorTextId : undefined}
              />
              {showClearButton && (
                <IconButton
                  aria-label="Clear search"
                  variant="ghost"
                  size="xs"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => handleClearSearch(e)}
                >
                  <Close />
                </IconButton>
              )}
              {showSearchButton && (
                <Button
                  variant="brand"
                  size="sm"
                  aria-label="Search"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    // Keep the click from bubbling to the PopoverTrigger (which would toggle it).
                    e.stopPropagation();
                    handleSearch();
                  }}
                >
                  <Search aria-hidden />
                </Button>
              )}
            </Box>
          </PopoverTrigger>
          <PopoverContent
            ref={popoverContentRef}
            w="full"
            bg="fill.input.enabled"
            borderRadius="md"
            borderWidth="1px"
            borderColor="stroke.border.container"
            boxShadow="0px 6px 24px 2px rgba(0, 0, 0, 0.06)"
            overflow="hidden"
            p="0"
          >
            {/* Figma scrolls the menu at 397px (node 313:31406) — roughly eight 48px
                rows, so a full Places section stays visible without a scroll. */}
            <Box maxH="397px" overflowY="auto">
              {resultsList}
            </Box>
          </PopoverContent>
        </PopoverRoot>
        {invalid && errorText && (
          <Box
            id={errorTextId}
            display="flex"
            alignItems="center"
            gap="1"
            color="text.error"
            role="alert"
            {...errorOverlayProps}
          >
            <ErrorIcon aria-hidden />
            <Text variant="SmallBodyMedium" color="text.error">
              {errorText}
            </Text>
          </Box>
        )}
      </Stack>
    );
  }

  const activateFromKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };
  const locationTriggerLabel = searchQuery.trim() || placeholder;

  return (
    <DialogRoot open={isOpen} onOpenChange={({ open }) => setIsOpen(open)} size="full">
      {/* Compact search bar. Hidden when the update sheet supplies the field. */}
      {!hideTrigger &&
        (triggerVariant === 'field' ? (
          <Stack gap="1" w="full" minW={0}>
            <Box
              display="flex"
              alignItems="center"
              gap="2"
              bg="fill.input.enabled"
              borderWidth="1px"
              borderColor={invalid ? 'stroke.border.error' : 'stroke.border.input'}
              borderRadius="md"
              px="4"
              py={leadingSuggestionType === 'event' && eventTimeLabel ? '3' : undefined}
              h={leadingSuggestionType === 'event' && eventTimeLabel ? undefined : '48px'}
              w="full"
              minW={0}
              maxW="100%"
              cursor="pointer"
              role="button"
              tabIndex={0}
              aria-label={
                invalid && errorText
                  ? `${locationTriggerLabel}. ${errorText}`
                  : locationTriggerLabel
              }
              onClick={activate}
              onKeyDown={activateFromKeyboard}
            >
              {suggestionTypeLeadingIcon(leadingSuggestionType)}
              {leadingSuggestionType === 'event' && eventTimeLabel ? (
                // Event context: the same search-bar-styled control shows the full
                // event title plus its time window as a second line, instead of a
                // plain single-line field (matches the results EventSearchSummary).
                <Stack gap="1" minW="0" flex="1">
                  <Text variant="MainBodyMedium" color="text.primary" truncate>
                    {locationTriggerLabel}
                  </Text>
                  <Text variant="SmallBodyRegular" color="text.secondary" truncate>
                    {eventTimeLabel}
                  </Text>
                </Stack>
              ) : (
                <Input
                  placeholder={placeholder}
                  value={searchQuery}
                  readOnly
                  tabIndex={-1}
                  pointerEvents="none"
                  onChange={() => undefined}
                  variant="flushed"
                  border="none"
                  bg="transparent"
                  cursor="pointer"
                  _focus={{ boxShadow: 'none', bg: 'transparent' }}
                  flex="1"
                  minW={0}
                  aria-invalid={invalid || undefined}
                  aria-describedby={invalid && errorText ? errorTextId : undefined}
                />
              )}
              {showClearButton && (
                <IconButton
                  aria-label="Clear location"
                  variant="ghost"
                  size="xs"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSearch(e);
                    setIsOpen(true);
                  }}
                >
                  <Close />
                </IconButton>
              )}
            </Box>
            {invalid && errorText && (
              <Box
                id={errorTextId}
                display="flex"
                alignItems="center"
                gap="1"
                color="text.error"
                role="alert"
              >
                <ErrorIcon aria-hidden />
                <Text variant="SmallBodyMedium" color="text.error">
                  {errorText}
                </Text>
              </Box>
            )}
          </Stack>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap="2"
            bg="fill.input.enabled"
            borderWidth="1px"
            borderColor="brand.400"
            borderRadius="md"
            pl="4"
            pr="2.5"
            h="56px"
            w="full"
            minW={0}
            maxW="100%"
            cursor="pointer"
            role="button"
            tabIndex={0}
            aria-label={locationTriggerLabel}
            onClick={activate}
            onKeyDown={activateFromKeyboard}
          >
            {suggestionTypeLeadingIcon(leadingSuggestionType)}
            <Input
              placeholder={placeholder}
              value={searchQuery}
              readOnly
              tabIndex={-1}
              pointerEvents="none"
              onChange={() => undefined}
              variant="flushed"
              border="none"
              bg="transparent"
              _focus={{ boxShadow: 'none', bg: 'transparent' }}
              flex="1"
              minW={0}
            />
            {showClearButton && (
              <IconButton
                aria-label="Clear search"
                variant="ghost"
                size="xs"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearSearch(e);
                  setIsOpen(true);
                }}
              >
                <Close />
              </IconButton>
            )}
            <Button
              variant="brand"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                (onActivate ?? handleSearch)();
              }}
              aria-label="Search"
            >
              <Search aria-hidden />
            </Button>
          </Box>
        ))}

      {/* Full-screen search dialog (rendered above the form via the dialog portal) */}
      <DialogContent
        ref={dialogContentRef}
        p="0"
        bg="fill.background.default"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          h="56px"
          borderBottomWidth="1px"
          borderColor="stroke.divider.default"
        >
          <Box w="56px" />
          <Text variant="MainBodyDemiBold" color="text.primary">
            Where to?
          </Text>
          <IconButton
            aria-label="Close search"
            variant="ghost"
            size="md"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIsOpen(false)}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Search input */}
        <Box px="4" pt="6">
          <Box
            display="flex"
            alignItems="center"
            gap="2"
            bg="fill.input.enabled"
            borderWidth="2px"
            borderColor="stroke.border.focused"
            borderRadius="md"
            px="4"
            h="56px"
            w="full"
            minW={0}
            maxW="100%"
          >
            <Search color="fill.icon.descriptive" aria-hidden />
            <Input
              ref={inputRef}
              autoFocus
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => {
                setDraftEdited(true);
                onSearchQueryChange(e.target.value);
              }}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              variant="flushed"
              border="none"
              bg="transparent"
              _focus={{ boxShadow: 'none', outline: 'none' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              flex="1"
              minW={0}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid && errorText ? errorTextId : undefined}
            />
            {showClearButton && (
              <IconButton
                aria-label="Clear search"
                variant="ghost"
                size="xs"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => handleClearSearch(e)}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Results list */}
        <Box flex="1" overflowY="auto">
          {resultsList}
        </Box>
      </DialogContent>
    </DialogRoot>
  );
};
